import {
  QueryFunctionContext,
  UseInfiniteQueryOptions,
  UseQueryOptions
} from '@tanstack/react-query';
import { AxiosMethods, ContactsApi, GroupsApi } from '../../../../enums';
import { axiosHelper } from '../../../axios-instance';
import {
  Contact,
  ContactDetails,
  ContactNote,
  GetContactsQuery,
  GetGroupContactsQuery,
  GetGroupsQuery,
  GetHubspotStatesQuery,
  GetNylasContactsQuery,
  GetRecentContactsQuery,
  GetSalesforceAccountsQuery,
  GetSalesforceCitiesQuery,
  GetSalesforceStatesQuery,
  HubspotCity,
  HubspotCompany,
  HubspotContact,
  NylasAccount
} from './types';
import { BaseQueryResponse } from '../../../../interfaces';

const createGetNextPageParam =
  <T,>(pageSize: number, getItems: (page: T) => any[]) =>
  (lastPage: T, allPages: T[]) => {
    const total = allPages.reduce((acc, cur) => acc + getItems(cur)?.length, 0);
    const currentPageNumber = total / pageSize;
    return getItems(lastPage)?.length < pageSize
      ? undefined
      : currentPageNumber + 1;
  };

export enum ContactsQueryKey {
  CONTACTS = 'contacts',
  GROUPS = 'groups',
  GROUP_CONTACTS = 'groupContacts',
  RECENT_CONTACTS = 'recentContacts',
  SALESFORCE_ACCOUNTS = 'salesforceAccounts',
  SALESFORCE_STATES = 'salesforceStates',
  SALESFORCE_CITIES = 'salesforceCities',
  SALESFORCE_CONTACTS = 'salesforceContacts',
  HUBSPOT_CONTACTS = 'hubspotContacts',
  HUBSPOT_COMPANIES = 'hubspotCompanies',
  HUBSPOT_STATES = 'hubspotStates',
  HUBSPOT_CITIES = 'hubspotCities',
  NYLAS_ACCOUNT = 'nylasAccount',
  NYLAS_CONTACTS = 'nylasContacts',
  CONTACT_DETAILS = 'contactDetails',
  CONTACT_NOTES = 'contactNotes'
}

const dynamicId = ':groupId';

interface GetContactsQueryParams {
  pageNumber: number;
  pageSize: number;
  orderBy: string;
  sortBy: string;
  search: string;
}

export const getContactsQuery = (
  params: GetContactsQueryParams
): UseInfiniteQueryOptions<GetContactsQuery> => ({
  queryKey: [ContactsQueryKey.CONTACTS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.Contacts,
      method: AxiosMethods.GET,
      params: { ...params, pageNumber: data.pageParam || params.pageNumber },
      baseURL: process.env.ADMIN_API_BASE_URL
    }).then(res => res?.Data),
  getNextPageParam: createGetNextPageParam<GetContactsQuery>(
    params.pageSize,
    page => page?.Items
  ),
  refetchOnWindowFocus: false
});

interface GetGroupsQueryParams {
  pageNumber: number;
  pageSize: number;
  orderBy: string;
  sortBy: string;
  search: string;
}

export const getGroupsQuery = (
  params: GetGroupsQueryParams
): UseInfiniteQueryOptions<GetGroupsQuery> => ({
  queryKey: [ContactsQueryKey.GROUPS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: GroupsApi.Groups,
      method: AxiosMethods.GET,
      params: { ...params, pageNumber: data.pageParam || params.pageNumber },
      baseURL: process.env.ADMIN_API_BASE_URL
    }).then(res => res?.Data),
  getNextPageParam: createGetNextPageParam<GetGroupsQuery>(
    params.pageSize,
    page => page?.Items
  ),
  refetchOnWindowFocus: false
});

interface GetGroupContactsQueryParams {
  groupId: number;
  pageNumber: number;
  pageSize: number;
  orderBy: string;
  sortBy: string;
  search: string;
}

export const getGroupsContactsQuery = (
  params: GetGroupContactsQueryParams
): UseInfiniteQueryOptions<GetGroupContactsQuery> => ({
  queryKey: [ContactsQueryKey.GROUP_CONTACTS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: GroupsApi.GroupContacts.replace(
        dynamicId,
        params.groupId.toString()
      ),
      method: AxiosMethods.GET,
      params: {
        pageSize: params.pageSize,
        pageNumber: data.pageParam || params.pageNumber,
        orderBy: params.orderBy,
        sortBy: params.sortBy,
        search: params.search
      },
      baseURL: process.env.ADMIN_API_BASE_URL
    }).then(res => res?.Data),
  getNextPageParam: createGetNextPageParam<GetGroupContactsQuery>(
    params.pageSize,
    page => page?.Items
  ),
  refetchOnWindowFocus: false
});

interface GetRecentContactsQueryParams {
  pageNumber: number;
  pageSize: number;
  sortBy: string;
  orderBy: string;
  search: string;
  sampleSize?: number;
}

export const getRecentContacts = (
  params: GetRecentContactsQueryParams
): UseInfiniteQueryOptions<GetRecentContactsQuery> => ({
  queryKey: [ContactsQueryKey.RECENT_CONTACTS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.RecentContacts,
      method: AxiosMethods.GET,
      params: { ...params, pageNumber: data.pageParam || params.pageNumber },
      baseURL: process.env.ADMIN_API_BASE_URL
    }).then(res => res?.Data),
  getNextPageParam: (
    lastPage: GetRecentContactsQuery,
    allPages: GetRecentContactsQuery[]
  ) => {
    const total = allPages?.reduce((acc, cur) => acc + cur?.Items?.length, 0);
    const currentPageNumber = total / params.pageSize;

    return lastPage?.Items?.length < params.pageSize ||
      total === lastPage.TotalCount
      ? undefined
      : currentPageNumber + 1;
  },
  refetchOnWindowFocus: false
});

export interface GetSalesfoceAccounts {
  pageSize: number;
  pageNumber: number;
  states: string[];
  cities: string[];
  searchString: string;
}

export const getSalesforceAccounts = (
  params: GetSalesfoceAccounts
): UseInfiniteQueryOptions<GetSalesforceAccountsQuery> => ({
  queryKey: [ContactsQueryKey.SALESFORCE_ACCOUNTS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.SalesforceAccounts,
      method: AxiosMethods.POST,
      body: {
        ...params,
        pageNumber: data.pageParam || params.pageNumber
      },
      baseURL: process.env.ADMIN_API_BASE_URL
    }),
  getNextPageParam: createGetNextPageParam<GetSalesforceAccountsQuery>(
    params.pageSize,
    page => page?.Data
  )
});

export const getSalesforceStates =
  (): UseQueryOptions<GetSalesforceStatesQuery> => ({
    queryKey: [ContactsQueryKey.SALESFORCE_STATES],
    queryFn: () =>
      axiosHelper({
        endpoint: ContactsApi.SalesforceStates,
        method: AxiosMethods.GET,
        baseURL: process.env.ADMIN_API_BASE_URL
      })
  });

export interface GetSalesforceCities {
  pageSize: number;
  pageNumber: number;
  states: string[];
  searchString: string;
}

export const getSalesforceCities = (
  params: GetSalesforceCities
): UseInfiniteQueryOptions<GetSalesforceCitiesQuery> => ({
  queryKey: [ContactsQueryKey.SALESFORCE_CITIES, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.SalesforceCities,
      method: AxiosMethods.POST,
      body: { ...params, pageNumber: data.pageParam || params.pageNumber },
      baseURL: process.env.ADMIN_API_BASE_URL
    }),
  getNextPageParam: createGetNextPageParam<GetSalesforceCitiesQuery>(
    params.pageSize,
    page => page?.Data
  )
});

export interface GetSalesforceContacts {
  pageSize: number;
  pageNumber: number;
  states: string[];
  cities: string[];
  accounts: string[];
  searchString: string;
}

export const getSalesforceContacts = (
  params: GetSalesforceContacts
): UseInfiniteQueryOptions<BaseQueryResponse<Contact[]>> => ({
  queryKey: [ContactsQueryKey.SALESFORCE_CONTACTS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.SalesforceContacts,
      method: AxiosMethods.POST,
      body: {
        ...params,
        pageNumber: data.pageParam || params.pageNumber
      },
      baseURL: process.env.ADMIN_API_BASE_URL
    }),
  getNextPageParam: createGetNextPageParam<BaseQueryResponse<Contact[]>>(
    params.pageSize,
    page => page?.Data
  )
});

export interface GetHubspotContacts {
  pageSize: number;
  pageNumber: number;
  searchString: string;
  companies: HubspotCompany['AssociatedCompanyId'][];
  cities: HubspotCompany['City'][];
  states: HubspotCompany['State'][];
}

export const getHubspotContacts = (
  params: GetHubspotContacts
): UseInfiniteQueryOptions<BaseQueryResponse<HubspotContact[]>> => ({
  queryKey: [ContactsQueryKey.HUBSPOT_CONTACTS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.HubspotContacts,
      method: AxiosMethods.POST,
      body: {
        ...params,
        pageNumber: data.pageParam || params.pageNumber
      },
      baseURL: process.env.ADMIN_API_BASE_URL
    }),
  getNextPageParam: createGetNextPageParam<BaseQueryResponse<HubspotContact[]>>(
    params.pageSize,
    page => page?.Data
  )
});

export interface GetHubspotProps {
  pageSize: number;
  pageNumber: number;
  searchString: string;
}

export const getHubspotCompanies = (
  params: GetHubspotProps
): UseInfiniteQueryOptions<BaseQueryResponse<HubspotCompany[]>> => ({
  queryKey: [ContactsQueryKey.HUBSPOT_COMPANIES, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.HubspotCompanies,
      method: AxiosMethods.POST,
      body: {
        ...params,
        pageNumber: data.pageParam || params.pageNumber
      },
      baseURL: process.env.ADMIN_API_BASE_URL
    }),
  getNextPageParam: createGetNextPageParam<BaseQueryResponse<HubspotCompany[]>>(
    params.pageSize,
    page => page?.Data
  )
});

export const getHubspotCities = (
  params: GetHubspotProps
): UseInfiniteQueryOptions<BaseQueryResponse<HubspotCity[]>> => ({
  queryKey: [ContactsQueryKey.HUBSPOT_CITIES, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.HubspotCities,
      method: AxiosMethods.POST,
      body: {
        ...params,
        pageNumber: data.pageParam || params.pageNumber
      },
      baseURL: process.env.ADMIN_API_BASE_URL
    }),
  getNextPageParam: createGetNextPageParam<BaseQueryResponse<HubspotCity[]>>(
    params.pageSize,
    page => page?.Data
  )
});

// ! This API returs a full list of states and have to be paginated when BC allows it
export const getHubspotStates = (): UseQueryOptions<GetHubspotStatesQuery> => ({
  queryKey: [ContactsQueryKey.HUBSPOT_STATES],
  queryFn: () =>
    axiosHelper({
      endpoint: ContactsApi.HubspotStates,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    })
});

export const getContactDetailsQuery = (
  contactId: number
): UseQueryOptions<ContactDetails> => ({
  queryKey: [ContactsQueryKey.CONTACT_DETAILS, contactId],
  queryFn: () =>
    axiosHelper({
      endpoint: ContactsApi.ContactDetails.replace(':id', contactId.toString()),
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    }).then(res => res?.Data),
  refetchOnWindowFocus: false
});

export const getContactNotesQuery = (
  userContactId: number
): UseQueryOptions<ContactNote[]> => ({
  queryKey: [ContactsQueryKey.CONTACT_NOTES, userContactId],
  queryFn: () =>
    axiosHelper({
      endpoint: ContactsApi.ContactNotes.replace(
        ':userContactId',
        userContactId.toString()
      ),
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    }).then(res => res?.Data),
  refetchOnWindowFocus: false
});

export const getNylasAccountQuery = (): UseQueryOptions<NylasAccount> => ({
  queryKey: [ContactsQueryKey.NYLAS_ACCOUNT],
  queryFn: () =>
    axiosHelper({
      endpoint: ContactsApi.NylasAccount,
      method: AxiosMethods.GET,
      baseURL: process.env.ADMIN_API_BASE_URL
    }).then(res => res?.Data),
  refetchOnWindowFocus: false
});

interface GetNylasContactsQueryParams {
  pageNumber: number;
  pageSize: number;
  orderBy: string;
  sortBy: string;
  search: string;
}

export const getNylasContactsQuery = (
  params: GetNylasContactsQueryParams
): UseInfiniteQueryOptions<GetNylasContactsQuery> => ({
  queryKey: [ContactsQueryKey.NYLAS_CONTACTS, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      endpoint: ContactsApi.NylasContacts,
      method: AxiosMethods.GET,
      params: { ...params, pageNumber: data.pageParam || params.pageNumber },
      baseURL: process.env.ADMIN_API_BASE_URL
    }).then(res => res?.Data),
  getNextPageParam: createGetNextPageParam<GetNylasContactsQuery>(
    params.pageSize,
    page => page?.Items
  ),
  refetchOnWindowFocus: false
});
